import { useEffect, useMemo, useState } from "react";

import { useUser } from "@components/user-context";
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from "@components/v2/buttons";
import { useModelInsightsKpiTimePeriod } from "@features/mmm/hooks/mmm-url-state";
import { type FetchModelInsights } from "@north-beam/nb-client-side";
import { ModelInsightsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import {
  getMMMEventSelectionData,
  MMMTab,
} from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { Modal } from "@shared/modal";

import { type ModelMetadataKpiFormula } from "../../explore-budgets/client-setting/types";
import { kpiPeriodValueToLabel } from "../kpi-period-selector";
import { exportChannelInsightsToCsv } from "../model-fit-chart/utils/export-to-csv";
import { type ChannelInsightsColumn } from "../model-insights-content";

import { ExportChannelInsightsPeriodSelector } from "./export-channel-insights-period-selector";
import { ExportChannelRow } from "./export-channel-row";
import { ExportChannelSelectAll } from "./export-channel-select-all";

interface Props {
  columns: ChannelInsightsColumn[];
  selectedKpi: ModelMetadataKpiFormula;
  costCurvesRawData: FetchModelInsights | undefined;
  isOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
}

export const SPEND_VS_REVENUE = "spend_vs_revenue" as const;
export const EFFICIENCY_OVER_TIME = "efficiency_over_time" as const;

export type ExportSelection = {
  [key: string]: {
    [SPEND_VS_REVENUE]: boolean;
    [EFFICIENCY_OVER_TIME]: boolean;
  };
};

export const ExportChannelInsightsModal = ({
  columns,
  selectedKpi,
  costCurvesRawData,
  isOpen,
  setIsModalOpen,
}: Props) => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.MODEL_INSIGHTS);
  const { user } = useUser();
  const [exportSelection, setExportSelection] = useState<ExportSelection>({});

  const [preselectedDateRange] = useModelInsightsKpiTimePeriod();
  const [dateRange, setDateRange] = useState(preselectedDateRange);
  useEffect(() => {
    setDateRange(preselectedDateRange);
  }, [preselectedDateRange, isOpen]);

  const costCurvesData = useMemo(
    () => costCurvesRawData?.me.mmm.modelInsightsCostCurves?.[dateRange] ?? {},
    [costCurvesRawData?.me.mmm.modelInsightsCostCurves, dateRange],
  );

  const historicalContributionsData = useMemo(
    () => costCurvesRawData?.me.mmm.modelInsightsHistoricalContributions ?? {},
    [costCurvesRawData?.me.mmm.modelInsightsHistoricalContributions],
  );

  useEffect(() => {
    setExportSelection(
      columns.reduce<ExportSelection>((acc, row) => {
        acc[row.column_name] = {
          [SPEND_VS_REVENUE]: true,
          [EFFICIENCY_OVER_TIME]: true,
        };
        return acc;
      }, {}),
    );
  }, [columns, isOpen]);

  return (
    <>
      <SecondaryButton
        className="mt-5 flex items-center justify-center gap-2"
        onClick={() => {
          trackMMMEvent({
            component:
              ModelInsightsTrackedComponent.EXPORT_CHANNEL_INSIGHTS_MODAL,
            action: "onOpen",
          });

          setIsModalOpen(true);
        }}
      >
        <i className="fa-regular fa-download text-cosmic" />
        EXPORT CHANNEL INSIGHTS
      </SecondaryButton>
      <Modal
        size="xl"
        isOpen={isOpen}
        onClose={() => {
          trackMMMEvent({
            component:
              ModelInsightsTrackedComponent.EXPORT_CHANNEL_INSIGHTS_MODAL,
            action: "onClose",
          });

          setIsModalOpen(false);
        }}
        titleContent={
          <div className="font-bold text-xl">Export channel insights</div>
        }
      >
        <div className="flex flex-col space-y-5">
          <ExportChannelInsightsPeriodSelector
            dateRange={dateRange}
            setDateRange={(value) => {
              trackMMMEvent({
                component:
                  ModelInsightsTrackedComponent.EXPORT_CHANNEL_INSIGHTS_PERIOD_SELECTOR,
                action: "onSelect",
                data: getMMMEventSelectionData(value),
              });

              setDateRange(value);
            }}
          />
          <ExportChannelSelectAll
            columns={columns}
            exportSelection={exportSelection}
            setExportSelection={setExportSelection}
          />
          <div className=" text-xs font-semibold items-center grid grid-cols-12 gap-1">
            <div className="text-base col-span-6">CHANNEL</div>
            <span className="text-center col-span-3">
              {`Incremental spend vs ${selectedKpi.friendly_name}`}
              <div className="rounded-full w-fit mt-2 mx-auto py-1 px-3.5 bg-pluto text-white text-xs">
                ALL TIME
              </div>
            </span>
            <span className="text-center col-span-3 ">
              Incremental performance over time
              <div className="rounded-full w-fit mt-2 mx-auto py-1 px-3.5 bg-cosmic text-white text-xs">
                {kpiPeriodValueToLabel(dateRange)}
              </div>
            </span>
          </div>
          <div className="max-h-[350px] overflow-y-scroll">
            <div className="text-sm grid grid-cols-12">
              {columns.map((column) => (
                <ExportChannelRow
                  key={column.column_name}
                  exportSelection={exportSelection}
                  setExportSelection={(value) => {
                    trackMMMEvent({
                      component:
                        ModelInsightsTrackedComponent.EXPORT_CHANNEL_INSIGHTS_CHANNEL_SELECTOR,
                      action: "onChange",
                    });

                    setExportSelection(value);
                  }}
                  column={column}
                />
              ))}
            </div>
          </div>
          <div className="pt-4 flex justify-end">
            <TertiaryButton
              onClick={() => {
                trackMMMEvent({
                  component:
                    ModelInsightsTrackedComponent.EXPORT_CHANNEL_INSIGHTS_MODAL,
                  action: "onClose",
                });

                setIsModalOpen(false);
              }}
              className="mr-2"
            >
              CANCEL
            </TertiaryButton>
            <PrimaryButton
              onClick={async () => {
                trackMMMEvent({
                  component:
                    ModelInsightsTrackedComponent.EXPORT_CHANNEL_INSIGHTS_MODAL,
                  action: "onSubmit",
                  actionInfo: "Export channel insights data",
                });

                await exportChannelInsightsToCsv(
                  dateRange,
                  exportSelection,
                  columns,
                  selectedKpi,
                  historicalContributionsData,
                  costCurvesData,
                  user,
                );
                setIsModalOpen(false);
              }}
            >
              EXPORT
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    </>
  );
};
